import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import logoiii from "../assets/logoiii.png";
import axios from "axios";
import UserDevices from "../components/userDevice";
import ChangePassword from "../components/ChangePassword";

const ProfileScreen = () => {
  const user = useSelector((state) => state.auth.user);
  const [activePlan, setActivePlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referralCodes, setReferralCodes] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [referralLoading, setReferralLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  useEffect(() => {
    const fetchActivePlan = async () => {
      if (!user?.id) return;
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/v1/userSubscriptions/${user.id}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        const now = new Date();
        const activeSubscription = response.data.find(sub =>
          sub.status === "active" &&
          new Date(sub.startDate) <= now &&
          new Date(sub.endDate) > now
        );
        if (activeSubscription) {
          setSubscriptionId(activeSubscription.id);
          const planResponse = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/v1/subscriptionPlans/${activeSubscription.subscriptionPlanId}`,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );
          setActivePlan({
            ...planResponse.data,
            id: activeSubscription.id,
            startDate: activeSubscription.startDate,
            endDate: activeSubscription.endDate
          });
        }
      } catch (error) {
        console.error("Error al obtener el plan activo:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchActivePlan();
  }, [user, cancelSuccess]);

  useEffect(() => {
    const fetchReferralData = async () => {
      if (!user?.id) return;
      try {
        setReferralLoading(true);
        const token = localStorage.getItem("token");
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
        const codesResponse = await axios.get(
          `${apiUrl}/api/v1/referral-codes/my-codes`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        
        // Obtener información de suscripciones para relacionarla con los códigos
        const subscriptionsResponse = await axios.get(
          `${apiUrl}/api/v1/userSubscriptions/${user.id}`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        
        // Obtener detalles de todos los planes de suscripción disponibles
        const plansResponse = await axios.get(
          `${apiUrl}/api/v1/subscriptionPlans`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        
        const plans = plansResponse.data || [];
        const subscriptions = subscriptionsResponse.data || [];
        const now = new Date();
        
        // Crear un mapa de planes para facilitar el acceso
        const plansMap = {};
        plans.forEach(plan => {
          plansMap[plan.id] = plan;
        });
        
        // Ordenar los códigos: primero los de suscripciones activas, luego canceladas pero válidas, etc.
        const enrichedCodes = codesResponse.data.map(code => {
          // Encontrar la suscripción relacionada con este código (por fecha de creación)
          const relatedSub = subscriptions.find(sub => 
            new Date(sub.startDate) <= new Date(code.createdAt) && 
            new Date(sub.endDate) >= new Date(code.createdAt)
          );
          
          // Si hay una suscripción relacionada, obtener su plan
          let usageLimit;
          if (relatedSub && plansMap[relatedSub.subscriptionPlanId]) {
            // Obtener el límite del plan relacionado
            const planDetails = plansMap[relatedSub.subscriptionPlanId];
            usageLimit = planDetails.referralLimit;
            console.log(`Plan ${planDetails.name} tiene límite de referidos: ${usageLimit}`);
          }
          
          // Si es usuario administrador, asignar un límite más alto
          if (user.role === 'admin') {
            usageLimit = 100;
          } else if (!usageLimit) {
            usageLimit = 10; // Valor predeterminado si no se encuentra un plan
          }
          
          return {
            ...code,
            subscriptionStatus: relatedSub ? relatedSub.status : null,
            isValidSubscription: relatedSub ? 
              (new Date(relatedSub.endDate) > now) : 
              (code.expiresAt ? new Date(code.expiresAt) > now : true),
            usageLimit: usageLimit
          };
        }).sort((a, b) => {
          // Ordenar por validez y estado de suscripción
          if (a.isValidSubscription !== b.isValidSubscription) {
            return a.isValidSubscription ? -1 : 1; // Válidos primero
          }
          if (a.subscriptionStatus !== b.subscriptionStatus) {
            return a.subscriptionStatus === "active" ? -1 : 1; // Activos primero
          }
          // Si todo es igual, ordenar por fecha de expiración (más recientes primero)
          return new Date(b.expiresAt || "9999-12-31") - new Date(a.expiresAt || "9999-12-31");
        });
        
        setReferralCodes(enrichedCodes);
      } catch (error) {
        console.error("Error al obtener referidos:", error.response?.data || error.message);
        setReferralCodes([]);
      } finally {
        setReferralLoading(false);
      }
    };
    fetchReferralData();
  }, [user]);

  const generateAdminReferralCode = async () => {
    try {
      setReferralLoading(true);
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      if (user?.role !== 'admin') {
        alert('Solo los administradores pueden generar códigos de referido admin');
        return;
      }
      await axios.post(`${apiUrl}/api/v1/referral-codes/admin`, {}, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const codesResponse = await axios.get(`${apiUrl}/api/v1/referral-codes/my-codes`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setReferralCodes(codesResponse.data);
      alert('Código de referido de administrador generado con éxito');
    } catch (error) {
      console.error("Error al generar código de admin:", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setReferralLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscriptionId) return;
    try {
      setCancelLoading(true);
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const response = await axios.put(`${apiUrl}/api/v1/userSubscriptions/user-cancel/${subscriptionId}`, {}, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setCancelSuccess(true);
      setShowCancelDialog(false);
      alert(response.data.message || "Tu suscripción ha sido cancelada.");
    } catch (error) {
      console.error("Error al cancelar la suscripción:", error);
      alert(`Error: ${error.response?.data?.message || "No se pudo cancelar la suscripción."}`);
    } finally {
      setCancelLoading(false);
    }
  };

  const renderReferralCodes = () => {
    return (
      <div className="mt-6">
        <h2 className="text-xl font-bold mb-3 text-white">
          Mis Códigos de Referido
        </h2>
        <p className="text-sm text-gray-300 mb-4">
          Comparte estos códigos con tus amigos para que obtengan beneficios al registrarse.
        </p>
        {referralLoading ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-white"></div>
            <p className="mt-2 text-gray-300">Cargando códigos de referido...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {referralCodes.length === 0 ? (
              <p className="text-center text-gray-400 py-4">
                No tienes códigos de referido activos.
              </p>
            ) : (
              referralCodes.map((code) => (
                <div 
                  key={code.id} 
                  className={`bg-gray-800 rounded-lg p-4 border-2 ${
                    code.active && code.isValidSubscription 
                      ? "border-green-500/30" 
                      : "border-gray-700"
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-mono text-lg font-semibold">{code.code}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(code.code);
                        alert('Código copiado al portapapeles');
                      }}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                    >
                      Copiar
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-gray-400">Estado:</div>
                    <div>
                      {code.active && code.isValidSubscription ? (
                        <span className="text-green-500">Activo</span>
                      ) : !code.active ? (
                        <span className="text-red-500">Inactivo</span>
                      ) : !code.isValidSubscription ? (
                        <span className="text-yellow-500">Expirará pronto</span>
                      ) : (
                        <span className="text-gray-500">Desconocido</span>
                      )}
                      
                      {code.subscriptionStatus === "cancelled" && code.isValidSubscription && (
                        <span className="ml-2 text-xs bg-blue-900 text-blue-300 px-2 py-0.5 rounded">
                          Plan cancelado pero vigente
                        </span>
                      )}
                    </div>
                    <div className="text-gray-400">Usos:</div>
                    <div>{code.usedCount || 0} / {code.usageLimit || "∞"}</div>
                    <div className="text-gray-400">Expira:</div>
                    <div>{code.expiresAt ? new Date(code.expiresAt).toLocaleDateString() : "No expira"}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="bg-gray-900 text-white min-h-screen flex items-center justify-center font-montserrat p-4 md:p-8 pt-24 sm:pt-28 md:pt-32" style={{ position: "relative", background: "linear-gradient(to bottom, #1a1a1a 0%, #0d0d0d 100%)", overflow: "hidden" }}>
        <div className="absolute inset-0 z-0" style={{ backgroundImage: `repeating-linear-gradient(90deg, transparent 0px, transparent 20px, #000000 20px, #000000 40px, transparent 40px, transparent 100px)`, backgroundSize: "100px 100%", boxShadow: "inset 0 0 10px rgba(0,0,0,0.8)" }} />
        <div className="absolute inset-0 z-0" style={{ backgroundImage: `repeating-linear-gradient(90deg, transparent 0px, transparent 20px, rgba(0,0,0,0.5) 20px, rgba(0,0,0,0.5) 40px, transparent 40px, transparent 100px)`, backgroundSize: "100px 100%", filter: "blur(3px)", transform: "translateX(2px)" }} />
        <div className="p-4 sm:p-6 md:p-8 rounded-2xl w-full max-w-lg relative z-10 mt-16 sm:mt-20 md:mt-5" style={{ border: "2px solid rgba(255, 255, 255, 0.7)", boxShadow: "0 0 20px rgba(255, 255, 255, 0.2)", background: "rgba(0, 0, 0, 0)", backdropFilter: "blur(5px)" }}>
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 sm:w-24 sm:h-24 mr-2">
              <img src={logoiii} alt="Logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-white font-bold text-lg sm:text-xl">EPIC MOVIE</span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-6 md:mb-8">Perfil de usuario</h1>

          {/* DATOS DEL USUARIO */}
          <div className="space-y-4 md:space-y-6">
            <div>
              <label className="block text-base md:text-lg font-medium text-white mb-2">Nombre</label>
              <p className="w-full p-2 md:p-3 bg-gray-800 border border-white/30 rounded-md text-white">{user?.name || "Cargando..."}</p>
            </div>

            <div>
              <label className="block text-base md:text-lg font-medium text-white mb-2">E-mail</label>
              <p className="w-full p-2 md:p-3 bg-gray-800 border border-white/30 rounded-md text-white">{user?.email || "Cargando..."}</p>
            </div>

            {/* PLAN ACTIVO */}
            <div>
              <label className="block text-base md:text-lg font-medium text-white mb-2">
                Plan Activo
              </label>
              {loading ? (
                <p className="w-full p-2 md:p-3 bg-gray-800 border border-white/30 rounded-md text-white">
                  Cargando información del plan...
                </p>
              ) : activePlan ? (
                <div className="w-full p-3 md:p-4 bg-gray-800 border border-white/30 rounded-md text-white">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-lg text-yellow-400">{activePlan.name}</span>
                    <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full">Activo</span>
                  </div>
                  <p className="text-sm text-gray-300 mb-2">{activePlan.description}</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-400">Precio:</span>
                      <span className="ml-2 text-white">${activePlan.price}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Duración:</span>
                      <span className="ml-2 text-white">{activePlan.duration || 30} días</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Inicio:</span>
                      <span className="ml-2 text-white">{new Date(activePlan.startDate).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Vencimiento:</span>
                      <span className="ml-2 text-white">{new Date(activePlan.endDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {/* Botón para cancelar la suscripción */}
                  <div className="mt-4 flex justify-end">
                    <button 
                      onClick={() => setShowCancelDialog(true)} 
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm transition-colors"
                    >
                      Cancelar suscripción
                    </button>
                  </div>
                </div>
              ) : (
                <div className="w-full p-3 md:p-4 bg-gray-800 border border-white/30 rounded-md text-white">
                  <p className="text-yellow-400 font-medium">No tienes un plan activo</p>
                  <p className="text-sm text-gray-300 mt-1">Contrata un plan para disfrutar de todas nuestras películas</p>
                  <a 
                    href="/subscriptionplan" 
                    className="mt-3 inline-block bg-yellow-500 hover:bg-yellow-600 text-black font-medium py-2 px-4 rounded-md text-sm transition-colors"
                  >
                    Ver planes disponibles
                  </a>
                </div>
              )}
            </div>

            {renderReferralCodes()}

            {/* REFERIDOS */}
            <div>
              <label className="block text-base md:text-lg font-medium text-white mb-2">Usuarios que referiste</label>
              {referralLoading ? (
                <p className="w-full p-2 md:p-3 bg-gray-800 border border-white/30 rounded-md text-white">Cargando tus referidos...</p>
              ) : referrals.length > 0 ? (
                <div className="w-full p-3 md:p-4 bg-gray-800 border border-white/30 rounded-md text-white">
                  <ul className="space-y-2">
                    {referrals.map((referral, index) => (
                      <li key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{referral.Referred?.name || 'Usuario'}</p>
                          <p className="text-xs text-gray-400">Registrado el {new Date(referral.createdAt).toLocaleDateString()}</p>
                        </div>
                        <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">Referido</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="w-full p-3 md:p-4 bg-gray-800 border border-white/30 rounded-md text-white">
                  <p className="text-yellow-400">Aún no has referido a ningún usuario</p>
                  <p className="text-sm text-gray-300 mt-1">Compartí tus códigos para comenzar a referir amigos.</p>
                </div>
              )}
            </div>

            {/* DISPOSITIVOS */}
            <div className="mt-6">
              <UserDevices />
            </div>

            {/* CAMBIO DE CONTRASEÑA */}
            <ChangePassword />
          </div>
        </div>
      </div>

      {/* MODAL CANCELAR SUSCRIPCIÓN */}
      {showCancelDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-70">
          <div className="bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-white mb-4">Cancelar suscripción</h3>
            <p className="text-gray-300 mb-6">¿Estás seguro que deseas cancelar tu suscripción? Podrás seguir utilizando el servicio hasta el final del período contratado.</p>
            <div className="flex justify-end space-x-4">
              <button onClick={() => setShowCancelDialog(false)} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700" disabled={cancelLoading}>No, mantener</button>
              <button onClick={handleCancelSubscription} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" disabled={cancelLoading}>
                {cancelLoading ? "Cancelando..." : "Sí, cancelar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileScreen;
